// Talmia 2.0 · Data Lab · Order detail — full BC + TM + Xero surface.
//
// Layered structure per user brief:
//   Hero header → KPI strip → Lifecycle timeline (parsed SystemNotes +
//   workflow flags) → Line-items table with supplier substitution sub-rows
//   → 4-way reconciliation (Customer · PO · Supplier · Xero) → Supplier
//   documents tabs (Confirmations w/ version history · Delivery notes ·
//   Invoices · Diff analysis HTML) → System-Notes audit trail → Right
//   rail (Customer, Supplier, Payment, Staff notes, Workflow flags,
//   Documents). Raw JSON kept at the bottom via DataAudit.
import { useEffect, useMemo, useState, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import {
  Package, Truck, FileText, CreditCard, Receipt, Factory,
  Building2, TrendingUp, Ticket, ExternalLink, MapPin, Send,
  CheckCircle2, XCircle, Clock, Bell, AlertTriangle, Paperclip,
  User as UserIcon, MessageSquare, ScrollText,
} from "lucide-react";
import { Panel, KV, Chip, BackLink, RawJson, money, dateGB, asText } from "./_shared";
import DataAudit from "./_dataAudit";
import LinkedRecords from "./LinkedRecords";
import { RecordHistory } from "./v2/_shared";

// ─── Utilities ────────────────────────────────────────────────────────
// Legacy Talmia stores DueDate/OrderDate as YYYYMMDD strings.
const parseYYYYMMDD = (s) => {
  if (!s || typeof s !== "string" || s.length < 8) return null;
  const y = s.slice(0, 4), m = s.slice(4, 6), d = s.slice(6, 8);
  return `${d}/${m}/${y}`;
};

// SystemNotes = "\r\n"-separated free-text lines with the pattern
//   "<event> by User <actor> on <Day DD Mon YYYY HH:MM:SS>"
const SN_RE = /^(.+?) by User (\S+) on (.+)$/;
const parseSystemNotes = (raw) => {
  if (!raw || typeof raw !== "string") return [];
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  return lines.map((line) => {
    const m = line.match(SN_RE);
    if (m) return { event: m[1], actor: m[2], when: m[3], raw: line };
    return { event: line, actor: null, when: null, raw: line };
  });
};

// Best-effort date pretty-print — supports both YYYYMMDD numeric-strings
// (Talmia legacy) and ISO / Date-parseable strings.
const anyDate = (s) => {
  if (!s) return null;
  if (typeof s === "string" && /^\d{8}$/.test(s)) return parseYYYYMMDD(s);
  const d = dateGB(s);
  return d && d !== "Invalid Date" ? d : null;
};

// BC pushes totals into Mongo as pre-formatted strings like "£10,851.84".
// Strip the currency + comma before Number() so reconciliation math works.
const parseMoney = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  const cleaned = String(v).replace(/[£$€,\s]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const statusTone = (s) => {
  const t = String(s || "").toLowerCase();
  if (t.includes("cancel"))       return "red";
  if (t.includes("incomplete"))   return "warn";
  if (t.includes("await"))        return "warn";
  if (t.includes("shipped") || t.includes("complete")) return "green";
  return "slate";
};

// ─── Sub-components ───────────────────────────────────────────────────
function KPI({ label, value, sub, tone = "" }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`text-xl font-mono font-bold ${tone || "text-white"}`}>{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function TimelineRow({ when, title, sub, tone = "slate" }) {
  const dotTone = {
    green: "bg-emerald-400 ring-emerald-400/30",
    warn:  "bg-amber-400 ring-amber-400/30",
    red:   "bg-rose-400 ring-rose-400/30",
    slate: "bg-slate-500 ring-slate-500/30",
  }[tone] || "bg-slate-500 ring-slate-500/30";
  return (
    <div className="flex items-start gap-3 py-2">
      <div className={`w-2.5 h-2.5 rounded-full ring-4 shrink-0 mt-1.5 ${dotTone}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate">{title}</div>
        {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
      </div>
      {when && <div className="text-[10.5px] font-mono text-slate-500 shrink-0">{when}</div>}
    </div>
  );
}

function LifecycleTimeline({ bc, tm }) {
  const rows = [];
  if (bc?.date_created)  rows.push({ when: dateGB(bc.date_created),  title: "Ordered", sub: `BC #${bc.id ?? "—"}`, tone: "slate" });
  if (tm?.POSent) rows.push({ when: anyDate(tm.PODate) || anyDate(tm.dt_date_created), title: "PO sent", sub: `${asText(tm.SupplierName)} · ${asText(tm.SupplierBranch)}`, tone: "warn" });
  (tm?.GoCs || []).forEach((g, i) => {
    const h = g?.Header || {};
    rows.push({
      when: parseYYYYMMDD(h.OrderDate) || null,
      title: `Confirmation v${h.Version ?? i}`,
      sub: `Due ${parseYYYYMMDD(h.DueDate) || "—"} · ${asText(h.OrderNumber)}`,
      tone: "warn",
    });
  });
  (tm?.GoDs || []).forEach((g) => {
    const h = g?.Header || {};
    rows.push({
      when: parseYYYYMMDD(h.DeliveryNoteCreated) || parseYYYYMMDD(h.DeliveryNoteDate) || null,
      title: `Delivery note ${asText(h.DeliveryNoteNumber)}`,
      sub: `${asText(h.OrderStatus)} · by ${asText(h.CreatedBy)}`,
      tone: "green",
    });
  });
  if (tm?.SentDispatch) rows.push({ when: anyDate(tm.dt_date_shipped) || null, title: "Dispatch sent", sub: "Marked shipped", tone: "green" });
  (tm?.GoIs || []).forEach((g) => {
    rows.push({
      when: g?.InvoiceDate || null,
      title: `Supplier invoice`,
      sub: `£${asText(g?.TotalAmount)} inc · DN ${asText(g?.DeliveryNoteNumber)}`,
      tone: "green",
    });
  });
  if (tm?.SentInvoice) rows.push({ when: null, title: "Customer invoice sent", tone: "green" });
  if (bc?.status)      rows.push({ when: anyDate(bc.date_modified), title: `Status → ${bc.status}`, tone: statusTone(bc.status) });

  if (rows.length === 0) return <div className="text-sm text-slate-500">No lifecycle events yet.</div>;
  return (
    <div className="divide-y divide-white/[0.03]">
      {rows.map((r, i) => <TimelineRow key={`tl-${i}`} {...r} />)}
    </div>
  );
}

function SupplierTabBtn({ id, label, count, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      data-testid={`supplier-tab-${id}`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-widest transition-colors ${
        active
          ? "bg-orange-500/[0.10] text-orange-200 border border-orange-500/40"
          : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
      }`}
    >
      {label} {count != null && <span className="text-slate-500">{count}</span>}
    </button>
  );
}

function SupplierDocsTabs({ tm }) {
  const gocs = tm?.GoCs || [];
  const gods = tm?.GoDs || [];
  const gois = tm?.GoIs || [];
  const hasDiff = !!(tm?.POSuppDiffAnalysis);

  const [tab, setTab] = useState(gocs.length ? "confirmations" : gods.length ? "notes" : gois.length ? "invoices" : hasDiff ? "diff" : "confirmations");

  return (
    <Panel icon={Factory} title="Supplier documents" right={`${asText(tm?.SupplierName)}${tm?.SupplierOrderNumber ? ` · ${tm.SupplierOrderNumber}` : ""}`} testid="panel-supp-docs">
      <div className="flex flex-wrap gap-1 mb-4">
        <SupplierTabBtn id="confirmations" label="Confirmations" count={gocs.length} active={tab === "confirmations"} onSelect={setTab} />
        <SupplierTabBtn id="notes"         label="Delivery notes" count={gods.length} active={tab === "notes"} onSelect={setTab} />
        <SupplierTabBtn id="invoices"      label="Invoices" count={gois.length} active={tab === "invoices"} onSelect={setTab} />
        {hasDiff && <SupplierTabBtn id="diff" label="Diff analysis" active={tab === "diff"} onSelect={setTab} />}
      </div>

      {tab === "confirmations" && (
        <div className="space-y-4">
          {gocs.length === 0 && <div className="text-sm text-slate-500">No confirmations received.</div>}
          {gocs.map((g, i) => {
            const h = g?.Header || {};
            const items = g?.Items || [];
            const total = g?.Total || {};
            const isLatest = i === gocs.length - 1;
            return (
              <div key={`goc-${i}`} className={`rounded-lg border ${isLatest ? "border-orange-500/40 bg-orange-500/[0.03]" : "border-white/10 bg-white/[0.02]"} p-3`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded font-mono text-[10.5px] uppercase tracking-widest ${isLatest ? "bg-orange-500/15 text-orange-200" : "bg-white/[0.03] text-slate-400"}`}>
                    v{h.Version ?? i} {isLatest && "· current"}
                  </span>
                  <span className="text-white text-sm font-semibold">{asText(h.OrderNumber)}</span>
                  <span className="text-xs text-slate-400 font-mono">Due {parseYYYYMMDD(h.DueDate)}</span>
                  <span className="text-xs text-slate-500 font-mono">Ordered {parseYYYYMMDD(h.OrderDate)}</span>
                  <span className="text-xs text-slate-500 ml-auto">by {asText(h.CreatedBy)} · {asText(h.GalaxyBranchDescription)}</span>
                </div>
                {items.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/10">
                          <th className="text-left py-1 pr-3">Code</th>
                          <th className="text-left py-1 pr-3">Description</th>
                          <th className="text-right py-1 pr-3">Qty</th>
                          <th className="text-right py-1 pr-3">Price</th>
                          <th className="text-right py-1 pr-3">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, j) => (
                          <tr key={`gi-${i}-${j}`} className="border-b border-white/5">
                            <td className="py-1 pr-3 font-mono text-[#C8A46B]">{asText(it.ProductCode)}</td>
                            <td className="py-1 pr-3 text-slate-300">{asText(it.ProductDescription)}</td>
                            <td className="py-1 pr-3 text-right font-mono">{asText(it.Quantity)} {asText(it.StockUnit)}</td>
                            <td className="py-1 pr-3 text-right font-mono">£{asText(it.Price)}/{asText(it.PriceUnit)}</td>
                            <td className="py-1 pr-3 text-right font-mono text-white">£{asText(it.Value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-white/5 flex justify-end gap-6 text-xs font-mono text-slate-400">
                  <span>Goods £{asText(total.TotalGoods)}</span>
                  <span>VAT £{asText(total.VAT)}</span>
                  <span className="text-white">Total £{asText(total.TotalAmount)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "notes" && (
        <div className="space-y-4">
          {gods.length === 0 && <div className="text-sm text-slate-500">No delivery notes received.</div>}
          {gods.map((g, i) => {
            const h = g?.Header || {};
            const items = g?.Items || [];
            return (
              <div key={`god-${i}`} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-white text-sm font-semibold">{asText(h.DeliveryNoteNumber)}</span>
                  <span className="text-xs text-slate-400 font-mono">Created {parseYYYYMMDD(h.DeliveryNoteCreated) || "—"}</span>
                  <span className="text-xs text-slate-400 font-mono">Delivery {parseYYYYMMDD(h.DeliveryNoteDate) || "—"}</span>
                  <Chip tone="green">{asText(h.OrderStatus)}</Chip>
                  <span className="text-xs text-slate-500 ml-auto">by {asText(h.CreatedBy)}</span>
                </div>
                {items.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/10">
                          <th className="text-left py-1 pr-3">Code</th>
                          <th className="text-left py-1 pr-3">Description</th>
                          <th className="text-right py-1 pr-3">Qty</th>
                          <th className="text-right py-1 pr-3">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, j) => (
                          <tr key={`gni-${i}-${j}`} className="border-b border-white/5">
                            <td className="py-1 pr-3 font-mono text-[#C8A46B]">{asText(it.ProductCode)}</td>
                            <td className="py-1 pr-3 text-slate-300">{asText(it.ProductDescription)}</td>
                            <td className="py-1 pr-3 text-right font-mono">{asText(it.Quantity)}</td>
                            <td className="py-1 pr-3 text-right font-mono text-white">£{asText(it.Value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "invoices" && (
        <div className="space-y-3">
          {gois.length === 0 && <div className="text-sm text-slate-500">No supplier invoices received.</div>}
          {gois.map((g, i) => (
            <div key={`goi-${i}`} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
              <KV label="Invoice date" value={<span className="font-mono text-xs">{asText(g?.InvoiceDate)}</span>} />
              <KV label="Your ref"     value={<span className="font-mono text-xs">{asText(g?.YourRef)}</span>} />
              <KV label="DN #"         value={<span className="font-mono">{asText(g?.DeliveryNoteNumber)}</span>} />
              <KV label="Goods"        value={<span className="font-mono">£{asText(g?.TotalGoods)}</span>} />
              <KV label="Total inc"    value={<span className="font-mono text-[#C8A46B]">£{asText(g?.TotalAmount)}</span>} />
            </div>
          ))}
        </div>
      )}

      {tab === "diff" && hasDiff && (
        <div
          className="prose prose-invert max-w-none prose-sm prose-p:my-1 prose-td:py-1"
          data-testid="posupp-diff-analysis"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tm.POSuppDiffAnalysis) }}
        />
      )}
    </Panel>
  );
}

function ReconciliationTable({ bc, tm, xero }) {
  const rows = [
    { stage: "Customer paid",     goods: parseMoney(bc?.subtotal_ex_tax), carriage: parseMoney(bc?.shipping_cost_ex_tax), vat: parseMoney(bc?.total_tax), total: parseMoney(bc?.total_inc_tax), delta: null, hint: "—" },
    { stage: "Purchase order",    goods: parseMoney(tm?.POSubTotalExcVAT), carriage: parseMoney(tm?.POPostageExcVAT), vat: parseMoney(tm?.POTotalVAT), total: parseMoney(tm?.POTotalIncVAT), delta: 0, hint: "baseline" },
    { stage: "Supplier conf",     goods: parseMoney(tm?.SupplierSubTotalExcVAT), carriage: parseMoney(tm?.SupplierPostageExcVAT), vat: parseMoney(tm?.SupplierTotalVAT), total: parseMoney(tm?.SupplierTotalIncVAT), delta: parseMoney(tm?.POSupplierDiff), hint: "" },
    { stage: "Xero AP",           goods: parseMoney(xero?.CommTotal || xero?.SubTotal), carriage: 0, vat: parseMoney(xero?.CommTotalTax || xero?.TotalTax), total: parseMoney(xero?.CommTotal || xero?.Total), delta: null, hint: xero?.Status || "—" },
  ];
  return (
    <Panel icon={Receipt} title="Financial reconciliation" right="Customer · PO · Supplier · Xero" testid="panel-recon">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/10">
              <th className="text-left py-2 pr-3">Stage</th>
              <th className="text-right py-2 pr-3">Goods ex VAT</th>
              <th className="text-right py-2 pr-3">Carriage</th>
              <th className="text-right py-2 pr-3">VAT</th>
              <th className="text-right py-2 pr-3">Total inc VAT</th>
              <th className="text-right py-2 pr-3">Δ vs PO</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.stage} className="border-b border-white/5">
                <td className="py-2 pr-3 text-slate-200">{r.stage}</td>
                <td className="py-2 pr-3 text-right font-mono">{money(r.goods)}</td>
                <td className="py-2 pr-3 text-right font-mono">{money(r.carriage)}</td>
                <td className="py-2 pr-3 text-right font-mono">{money(r.vat)}</td>
                <td className="py-2 pr-3 text-right font-mono text-white">{money(r.total)}</td>
                <td className={`py-2 pr-3 text-right font-mono ${r.delta === 0 ? "text-slate-400" : r.delta < 0 ? "text-emerald-300" : r.delta > 0 ? "text-rose-300" : "text-slate-500"}`}>
                  {r.delta == null ? r.hint : (r.delta === 0 ? "baseline" : `${r.delta > 0 ? "+" : ""}${money(r.delta)}`)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tm?.POSupplierDiff != null && Math.abs(Number(tm.POSupplierDiff)) > 0 && (
        <div className={`mt-3 text-[11px] font-mono ${tm.SuppPODiffOK ? "text-emerald-300" : "text-amber-300"}`}>
          {tm.SuppPODiffOK ? "PO/Supp diff acknowledged" : "PO/Supp diff not marked addressed"}
          {" · "}
          {tm.XeroReconciled ? "Xero reconciled" : "Xero not reconciled"}
        </div>
      )}
    </Panel>
  );
}

function LineItemsWithSubstitution({ bc, tm }) {
  // BigCommerce responses can either inline products under `BCOrder.products`
  // OR store just a sub-resource link `{url,resource}`. When the inline array
  // is missing, `TMOrder.Products` holds the exact same BC line-item shape
  // (lowercase `sku`, `price_ex_tax`, `base_cost_price`, `product_options` …).
  const bcInline = Array.isArray(bc?.products) ? bc.products
                : Array.isArray(bc?.line_items) ? bc.line_items
                : [];
  const tmProds = Array.isArray(tm?.Products) ? tm.Products : [];
  const items = bcInline.length > 0 ? bcInline : tmProds;

  // Match TM product to BC line by SKU (best-effort) so we can render
  // a "supplied-as" sub-row inline when there's supplier-code enrichment.
  const bySku = useMemo(() => {
    const m = new Map();
    tmProds.forEach((p) => {
      const k = p.sku || p.SKU || p.Sku;
      if (k) m.set(String(k), p);
    });
    return m;
  }, [tmProds]);

  const subtotal = items.reduce((a, li) => a + parseMoney(li.total_ex_tax || (parseMoney(li.price_ex_tax) * Number(li.quantity || 0))), 0);
  const totalQty = items.reduce((a, i) => a + Number(i.quantity || 0), 0);

  return (
    <Panel icon={Package} title="Line items · Customer ↔ Supplier mapping" right={`${items.length} lines · ${totalQty} units`} testid="panel-lines">
      {items.length === 0 ? (
        <div className="text-sm text-slate-500">No line items.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/10">
                <th className="text-left py-2 pr-3">Product</th>
                <th className="text-right py-2 pr-3">Qty</th>
                <th className="text-right py-2 pr-3">Sell ex</th>
                <th className="text-right py-2 pr-3">Cost ex</th>
                <th className="text-right py-2 pr-3">Line total ex</th>
                <th className="text-right py-2 pr-3">Margin</th>
              </tr>
            </thead>
            <tbody>
              {items.map((li, i) => {
                const sell = parseMoney(li.price_ex_tax);
                const cost = parseMoney(li.base_cost_price);
                const qty  = Number(li.quantity || 0);
                const total = parseMoney(li.total_ex_tax || sell * qty);
                const marginPct = sell > 0 ? ((sell - cost) / sell) * 100 : 0;
                const tmMatched = bySku.get(String(li.sku));
                const opts = Array.isArray(li.product_options) ? li.product_options : [];
                return (
                  <Fragment key={`li-${i}-${li.id ?? li.sku ?? i}`}>
                    <tr className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-2 pr-3">
                        <div className="font-mono text-[#C8A46B] text-xs">{asText(li.sku)}</div>
                        <div className="text-slate-100 line-clamp-2">
                          {li.product_id ? <Link to={`/lab/products/${li.product_id}`} className="hover:text-white">{asText(li.name)}</Link> : asText(li.name)}
                        </div>
                        {opts.length > 0 && (
                          <div className="text-[10.5px] text-slate-500 mt-0.5">
                            {opts.map((o, j) => (
                              <div key={`o-${i}-${j}`}>⚑ {o.display_name || o.name}: <span className="text-slate-300">{o.display_value || o.value}</span></div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">{qty}</td>
                      <td className="py-2 pr-3 text-right font-mono">{money(sell)}</td>
                      <td className="py-2 pr-3 text-right font-mono text-slate-400">{cost ? money(cost) : "—"}</td>
                      <td className="py-2 pr-3 text-right font-mono text-white">{money(total)}</td>
                      <td className={`py-2 pr-3 text-right font-mono ${marginPct > 10 ? "text-emerald-300" : marginPct > 0 ? "text-amber-300" : "text-slate-500"}`}>
                        {marginPct ? `${marginPct.toFixed(1)}%` : "—"}
                      </td>
                    </tr>
                    {tmMatched && (tmMatched.SupplierProductCode || tmMatched.SupplierProductDescription) && (
                      <tr className="border-b border-white/5 bg-white/[0.015]">
                        <td colSpan={6} className="py-2 pr-3 pl-6 text-[11.5px] text-slate-400">
                          ↳ supplied as <span className="font-mono text-[#C8A46B]">{asText(tmMatched.SupplierProductCode)}</span>
                          {tmMatched.SupplierProductDescription && <> · {asText(tmMatched.SupplierProductDescription)}</>}
                          {tmMatched.Quantity != null && <> · {asText(tmMatched.Quantity)} @ £{asText(tmMatched.SupplierCostExcVAT ?? tmMatched.SupplierPriceExcVAT)}/{asText(tmMatched.StockUnit || "unit")}</>}
                          {tmMatched.Margin != null && <> · margin {money(tmMatched.Margin)}</>}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              <tr className="bg-white/[0.03]">
                <td className="py-2 pr-3 text-[10.5px] font-mono uppercase tracking-widest text-slate-500">Totals</td>
                <td className="py-2 pr-3 text-right font-mono text-slate-400">{totalQty}</td>
                <td></td>
                <td></td>
                <td className="py-2 pr-3 text-right font-mono text-white font-semibold">{money(subtotal)}</td>
                <td className="py-2 pr-3 text-right font-mono text-white font-semibold">{tm?.Margin != null ? `${Number(tm.Margin).toFixed(2)}%` : "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function AuditTrail({ raw }) {
  const events = useMemo(() => parseSystemNotes(raw), [raw]);
  if (events.length === 0) return null;
  return (
    <Panel icon={ScrollText} title="System notes · Audit trail" right={`${events.length} events`} testid="panel-audit">
      <div className="divide-y divide-white/[0.03] max-h-96 overflow-y-auto pr-2">
        {events.map((e, i) => (
          <div key={`sn-${i}`} className="py-2 flex items-start gap-3 text-sm">
            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${e.actor === "Talmia" ? "bg-teal-400" : "bg-orange-400"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-slate-200">{e.event}</div>
              {(e.actor || e.when) && (
                <div className="text-[10.5px] text-slate-500 font-mono mt-0.5">
                  {e.actor && <span className={e.actor === "Talmia" ? "text-teal-300" : "text-orange-300"}>{e.actor}</span>}
                  {e.actor && e.when && <span className="opacity-40 mx-1">·</span>}
                  {e.when}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────
export default function LabOrderDetail() {
  const { id } = useParams();
  const [doc, setDoc]   = useState(null);
  const [loading, setL] = useState(true);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setL(true);
        const { data } = await api.get(`/lab/orders/${id}`);
        if (alive) setDoc(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load order");
      } finally { if (alive) setL(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) return <Layout><div className="text-center text-slate-500 py-20">Loading order…</div></Layout>;
  if (err || !doc) return <Layout><div className="max-w-xl mx-auto text-center py-20 text-red-400">{err || "Order not found"}</div></Layout>;

  const bc   = doc.BCOrder || {};
  const tm   = doc.TMOrder || {};
  const xero = doc.XeroInvoice || {};
  const bill = bc.billing_address || {};
  const ship = (bc.shipping_addresses || [])[0] || {};
  const displayName = [bill.first_name, bill.last_name].filter(Boolean).join(" ") || bill.company || `Order ${doc._id}`;

  return (
    <Layout>
      <div className="space-y-6" data-testid="lab-order-detail-page">
        <BackLink to="/lab/orders" label="Data Lab · Orders" />

        {/* HERO */}
        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-[#0F1420] via-[#0F1420] to-[#1a1223] p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Chip tone="orange">Order #{asText(bc.id ?? doc._id)}</Chip>
                {bc.status && <Chip tone={statusTone(bc.status)}>{bc.status}</Chip>}
                {tm.VIPLevel && <Chip tone="warn">★ {tm.VIPLevel}</Chip>}
                {tm.DirectOrder && <Chip tone="blue">Direct</Chip>}
                {tm.IsParked && <Chip tone="warn">Parked{tm.ParkedReason ? ` · ${tm.ParkedReason}` : ""}</Chip>}
                {tm.Archived && <Chip tone="slate">Archived</Chip>}
                {tm.SentInvoice && <Chip tone="green">Invoiced</Chip>}
                {tm.SentDispatch && <Chip tone="green">Dispatched</Chip>}
                {tm.XeroReconciled && <Chip tone="green">Xero reconciled</Chip>}
                {tm.XeroReconciled === false && <Chip tone="red">⚠ Xero not reconciled</Chip>}
                {tm.SuppPODiffOK === false && <Chip tone="red">⚠ PO/Supp diff unaddressed</Chip>}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight break-words">{displayName}</h1>
              {bill.company && (
                <div className="text-sm text-slate-300 mt-1 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-slate-500" />{bill.company}
                </div>
              )}
              <div className="text-xs text-slate-400 mt-1">
                {bill.email && <><a href={`mailto:${bill.email}`} className="text-[#C8A46B] hover:text-white">{bill.email}</a> · </>}
                {bill.phone && <>{bill.phone} · </>}
                {[bill.city, bill.zip].filter(Boolean).join(" ")}
                {bc.customer_id === 0 && <> · <span className="text-amber-300">Guest checkout</span></>}
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-2 text-xs text-slate-500 font-mono">
                <span>Doc {asText(doc._id)}</span>
                {bc.date_created && <span>Created {dateGB(bc.date_created)}</span>}
                {bc.date_modified && <span>Updated {dateGB(bc.date_modified)}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Total inc VAT</div>
              <div className="text-4xl font-bold font-mono text-orange-400">{money(parseMoney(bc.total_inc_tax))}</div>
              <div className="text-xs text-slate-400 mt-1 font-mono">
                {money(parseMoney(bc.subtotal_ex_tax))} ex · {money(parseMoney(bc.total_tax))} VAT · {bc.items_total || 0} items
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-5 pt-4 border-t border-white/5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPI label="Revenue ex VAT"   value={money(parseMoney(bc.subtotal_ex_tax))} sub="Customer total" tone="text-white" />
            <KPI label="Supplier cost"    value={money(parseMoney(tm.SupplierTotalExcVAT || tm.SupplierSubTotalExcVAT))} sub={`${asText(tm.SupplierName) || "—"} confirmed`} />
            <KPI label="Profit ex VAT"    value={money(parseMoney(tm.Profit))} sub="System figure" tone={parseMoney(tm.Profit) > 0 ? "text-emerald-300" : "text-rose-300"} />
            <KPI label="Margin"           value={tm.Margin != null ? `${Number(tm.Margin).toFixed(2)}%` : "—"} sub="System figure" />
            <KPI label="PO / Supp diff"   value={money(parseMoney(tm.POSupplierDiff))} sub={parseMoney(tm.POSupplierDiff) < 0 ? "Supplier cheaper" : parseMoney(tm.POSupplierDiff) > 0 ? "Supplier dearer" : "Match"} tone={parseMoney(tm.POSupplierDiff) < 0 ? "text-emerald-300" : parseMoney(tm.POSupplierDiff) > 0 ? "text-rose-300" : ""} />
            <KPI label="Refunded"         value={money(parseMoney(bc.refunded_amount))} sub={parseMoney(bc.refunded_amount) > 0 ? "Returns pending" : "No returns"} />
          </div>
        </div>

        {/* Lifecycle timeline */}
        <Panel icon={Clock} title="Lifecycle timeline" testid="panel-timeline">
          <LifecycleTimeline bc={bc} tm={tm} />
        </Panel>

        {/* Line items */}
        <LineItemsWithSubstitution bc={bc} tm={tm} />

        {/* Reconciliation */}
        <ReconciliationTable bc={bc} tm={tm} xero={xero} />

        {/* Supplier documents tabs */}
        {((tm.GoCs || []).length > 0 || (tm.GoDs || []).length > 0 || (tm.GoIs || []).length > 0 || tm.POSuppDiffAnalysis) && (
          <SupplierDocsTabs tm={tm} />
        )}

        {/* Two-col: audit trail (main) + right rail */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <AuditTrail raw={tm.SystemNotes} />
            <RawJson
              title="Raw source"
              testid="panel-raw"
              sources={[
                { key: "tm", label: "TMOrder (enriched)", data: tm, note: "Enriched view — money already numeric & sortable." },
                { key: "bc", label: "BCOrder (raw)", data: bc, note: "⚠ Raw view (BCOrder) — money stored as £-strings; coerced to numbers for sorting/reconciliation." },
                { key: "xero", label: "XeroInvoice", data: xero, note: null },
              ]}
            />
          </div>

          <div className="space-y-6">
            {/* Customer & delivery */}
            <Panel icon={UserIcon} title="Customer & delivery" right="delivery stored 3× in source" testid="panel-customer">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Bill to</div>
              <div className="text-sm text-white font-semibold">{asText(bill.first_name)} {asText(bill.last_name)}</div>
              <div className="text-xs text-slate-400">{[bill.street_1, bill.city, bill.zip].filter(Boolean).join(", ")}</div>
              {ship.street_1 && ship.street_1 !== bill.street_1 && (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mt-3 mb-1">Ship to</div>
                  <div className="text-sm text-white font-semibold">{asText(ship.first_name)} {asText(ship.last_name)}</div>
                  <div className="text-xs text-slate-400">{[ship.street_1, ship.city, ship.zip].filter(Boolean).join(", ")}</div>
                </>
              )}
              {ship.shipping_method && <KV label="Method" value={ship.shipping_method} />}
              {ship.tracking_number && <KV label="Tracking" value={<span className="font-mono">{ship.tracking_number}</span>} />}
            </Panel>

            {/* Supplier */}
            <Panel icon={Factory} title="Supplier" testid="panel-supplier">
              <KV label="Name"       value={<span className="text-white font-semibold">{asText(tm.SupplierName)}</span>} wide />
              <KV label="Branch"     value={asText(tm.SupplierBranch)} />
              <KV label="Order no."  value={<span className="font-mono">{asText(tm.SupplierOrderNumber)}</span>} />
              <KV label="Account"    value={<span className="font-mono">{asText((tm.GoCs?.[0]?.Header?.CustomerNo) ?? "—")}</span>} />
              <KV label="Split"      value={asText(tm.POSplit)} />
              {tm.SupplierMessage && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Message to supplier</div>
                  <div className="text-xs text-slate-300 whitespace-pre-wrap">{tm.SupplierMessage}</div>
                </div>
              )}
            </Panel>

            {/* Payment */}
            <Panel icon={CreditCard} title="Payment" testid="panel-payment">
              <div className="grid grid-cols-2 gap-3">
                <KV label="Provider"   value={<span className="font-mono">{asText(bc.payment_provider_id) || asText(bc.payment_method)}</span>} />
                <KV label="Method"     value={<span className="font-mono">{asText(bc.payment_method)}</span>} />
                <KV label="Status"     value={<span className="font-mono">{asText(bc.payment_status)}</span>} />
                <KV label="Card"       value={<span className="font-mono">{asText(bc.credit_card_type)}</span>} />
                <KV label="Currency"   value={<span className="font-mono">{asText(bc.currency_code)}</span>} />
                <KV label="Paid"       value={<span className="font-mono text-[#C8A46B]">{money(parseMoney(bc.total_inc_tax))}</span>} />
                <KV label="Refunded"   value={<span className="font-mono">{money(parseMoney(bc.refunded_amount))}</span>} />
                <KV label="Store cred" value={<span className="font-mono">{money(parseMoney(bc.store_credit_amount))}</span>} />
              </div>
              {Array.isArray(bc.coupons) && bc.coupons.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Coupons · {bc.coupons.length}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {bc.coupons.map((c, i) => (
                      <div key={`cp-${i}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/[0.05]">
                        <Ticket className="w-3 h-3 text-amber-400" />
                        <span className="font-mono text-[11px] text-amber-300">{c.code}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Panel>

            {/* Xero */}
            {(xero._id || xero.InvoiceNumber) && (
              <Panel icon={FileText} title="Xero AP" testid="panel-xero">
                <div className="grid grid-cols-2 gap-3">
                  <KV label="Number"   value={<span className="font-mono">{asText(xero.InvoiceNumber)}</span>} />
                  <KV label="Status"   value={<span className="font-mono">{asText(xero.Status)}</span>} />
                  <KV label="Total"    value={<span className="font-mono">{money(xero.Total || xero.CommTotal)}</span>} />
                  <KV label="Tax"      value={<span className="font-mono">{money(xero.TotalTax || xero.CommTotalTax)}</span>} />
                  <KV label="Currency" value={<span className="font-mono">{asText(xero.CurrencyCode)}</span>} />
                  <KV label="Date"     value={<span className="font-mono text-xs">{asText(xero.Date)}</span>} />
                </div>
              </Panel>
            )}

            {/* Staff notes */}
            {tm.StaffNotes && (
              <Panel icon={MessageSquare} title="Staff notes" testid="panel-staff-notes">
                <div className="text-sm text-slate-200 whitespace-pre-wrap">{tm.StaffNotes}</div>
              </Panel>
            )}

            {/* Workflow flags */}
            <Panel icon={CheckCircle2} title="Workflow state" testid="panel-workflow">
              <div className="flex flex-wrap gap-1.5">
                {[
                  ["PO sent",         tm.POSent],
                  ["Conf received",   tm.ConfReceived],
                  ["Order update sent", tm.SentOrderUpdate],
                  ["Dispatch sent",   tm.SentDispatch],
                  ["Invoice sent",    tm.SentInvoice],
                  ["Parked",          tm.IsParked],
                  ["Xero reconciled", tm.XeroReconciled],
                  ["Archived",        tm.Archived],
                ].map(([label, v]) => (
                  <span key={label} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-mono uppercase tracking-widest ${
                    v ? "border-emerald-400/40 bg-emerald-500/[0.06] text-emerald-200" : "border-white/10 bg-white/[0.02] text-slate-500"
                  }`}>
                    {v ? <CheckCircle2 className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
                    {label}
                  </span>
                ))}
              </div>
            </Panel>

            {/* Documents (file paths from TM) */}
            {(tm.POFilePath || tm.DIFilePath || tm.InvoiceFilePath) && (
              <Panel icon={Paperclip} title="Documents" testid="panel-docs">
                <div className="space-y-1.5 text-xs">
                  {tm.POFilePath && <div className="font-mono text-[#C8A46B] break-all">{tm.POFilePath}</div>}
                  {tm.DIFilePath && <div className="font-mono text-[#C8A46B] break-all">{tm.DIFilePath}</div>}
                  {tm.InvoiceFilePath && <div className="font-mono text-[#C8A46B] break-all">{tm.InvoiceFilePath}</div>}
                </div>
              </Panel>
            )}
          </div>
        </div>
      </div>

      <LinkedRecords collection="orders" id={id} />
      <DataAudit doc={doc} title="Data audit — full record" testid="lab-data-audit" />
      <RecordHistory collection="orders" docId={id} testid="lab-order-history" />
    </Layout>
  );
}
